/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Builder credits banner + application dialog. Ported from packages/ui
// (BuilderCredits.tsx) so customer-portal builds inside its own Docker
// context; behaviour and validation are unchanged, styling uses console tokens.
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogPanel, DialogTitle, Description } from '@headlessui/react';
import { Button, Tag } from '../console/ui';
import { BoltIc } from '../console/icons';

export interface BuilderCreditsFormData {
  email: string;
  project_description: string;
  github_url?: string;
  colab_url?: string;
  linkedin_url?: string;
  phone?: string;
}

const EMPTY: BuilderCreditsFormData = {
  email: '',
  project_description: '',
  github_url: '',
  colab_url: '',
  linkedin_url: '',
  phone: '',
};

const DISPOSABLE_DOMAINS = new Set(['mailinator.com', 'guerrillamail.com', 'tempmail.com', 'yopmail.com', '10minutemail.com']);

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isDisposable = (email: string) => {
  const domain = email.split('@')[1]?.toLowerCase();
  return domain ? DISPOSABLE_DOMAINS.has(domain) : false;
};

const BuilderCreditsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (data: BuilderCreditsFormData) => Promise<void>;
  kycEnabled?: boolean;
}> = ({ open, onClose, onSubmit, kycEnabled = false }) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<BuilderCreditsFormData>(EMPTY);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setError('');
      setLoading(false);
      setForm(EMPTY);
    }
  }, [open]);

  const canContinue = useMemo(() => {
    if (!isValidEmail(form.email) || isDisposable(form.email)) return false;
    if (form.project_description.trim().length < 20) return false;
    if (kycEnabled && step === 2 && (!form.phone || form.phone.length < 8)) return false;
    return true;
  }, [form, kycEnabled, step]);

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      await onSubmit(form);
      setStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/70" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="ps-card w-full max-w-md p-6 flex flex-col gap-4">
          {step === 1 && (
            <>
              <div>
                <DialogTitle className="m-0 text-xl font-medium">Apply for builder credits</DialogTitle>
                <Description className="mt-1.5 text-sm text-ps-muted">
                  ₹500 free for serious builders. Apply with your project - approved in 2 hours.
                </Description>
              </div>
              <div>
                <label className="ps-label" htmlFor="bc-email">Email</label>
                <input
                  id="bc-email"
                  type="email"
                  className="ps-input"
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <label className="ps-label" htmlFor="bc-desc">What are you building?</label>
                <textarea
                  id="bc-desc"
                  className="ps-input min-h-[100px] py-2.5"
                  placeholder="Fine-tuning Llama 3-8B for Hindi medical QA..."
                  value={form.project_description}
                  onChange={(e) => setForm({ ...form, project_description: e.target.value })}
                />
                <p className="mt-1 text-xs text-ps-subtle">Min 20 characters. Crypto workloads are prohibited.</p>
              </div>
              <div>
                <label className="ps-label" htmlFor="bc-gh">GitHub / Colab link (optional)</label>
                <input
                  id="bc-gh"
                  className="ps-input"
                  placeholder="https://github.com/..."
                  value={form.github_url}
                  onChange={(e) => setForm({ ...form, github_url: e.target.value })}
                />
              </div>
              {error && <p className="text-sm text-ps-err">{error}</p>}
              {kycEnabled ? (
                <Button onClick={() => setStep(2)} disabled={!canContinue || loading} className="w-full">
                  Continue
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={!canContinue || loading} className="w-full">
                  {loading ? 'Submitting…' : 'Submit application'}
                </Button>
              )}
            </>
          )}

          {step === 2 && kycEnabled && (
            <>
              <div>
                <DialogTitle className="m-0 text-xl font-medium">Verify your identity</DialogTitle>
                <Description className="mt-1.5 text-sm text-ps-muted">
                  Phone verification unlocks larger credit allocations.
                </Description>
              </div>
              <div>
                <label className="ps-label" htmlFor="bc-phone">Phone number</label>
                <input
                  id="bc-phone"
                  type="tel"
                  className="ps-input"
                  placeholder="+91 98765 43210"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="ps-label" htmlFor="bc-li">LinkedIn profile (optional)</label>
                <input
                  id="bc-li"
                  className="ps-input"
                  placeholder="https://linkedin.com/in/..."
                  value={form.linkedin_url}
                  onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                />
              </div>
              {error && <p className="text-sm text-ps-err">{error}</p>}
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button variant="secondary" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button onClick={handleSubmit} disabled={!canContinue || loading}>
                  {loading ? 'Submitting…' : 'Submit application'}
                </Button>
              </div>
            </>
          )}

          {step === 3 && (
            <div className="flex flex-col items-center py-4 text-center gap-2">
              <DialogTitle className="m-0 text-xl font-medium">Application received</DialogTitle>
              <Description className="text-sm text-ps-muted">
                We review builder applications within 2 hours. Check your email for next steps.
              </Description>
              <Button onClick={onClose} className="mt-4">
                Got it
              </Button>
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export const BuilderCreditsBanner: React.FC<{
  onApply: (data: BuilderCreditsFormData) => Promise<void>;
  kycEnabled?: boolean;
}> = ({ onApply, kycEnabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <section
      aria-label="Builder credits"
      className="ps-card px-4 py-3 sm:px-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between !bg-[#140C06] !border-[#5B2A0E]"
    >
      <div className="flex items-start gap-3">
        <span className="ps-icb w-9 h-9">
          <BoltIc />
        </span>
        <div>
          <p className="m-0 text-sm sm:text-base font-semibold text-ps-text">
            ₹500 free for serious builders <Tag tone="accent" className="ml-1 align-middle">Limited</Tag>
          </p>
          <p className="m-0 text-xs sm:text-sm text-ps-muted">
            Apply with your project - approved in 2 hours. Crypto workloads prohibited.
          </p>
        </div>
      </div>
      <Button size="sm" onClick={() => setOpen(true)} className="w-full sm:w-auto">
        Apply now
      </Button>
      <BuilderCreditsDialog open={open} onClose={() => setOpen(false)} onSubmit={onApply} kycEnabled={kycEnabled} />
    </section>
  );
};
